/**
 * Content Manager - Layout Component
 *
 * Main layout with AppBar, Sidebar, and content area.
 * Uses the Highland design system colors and patterns.
 */

import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  useMediaQuery,
  useTheme,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  AdminPanelSettings as AdminPanelSettingsIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import {
  FileText,
  Calendar,
  CalendarDays,
  LayoutDashboard,
  FolderTree,
  Users,
  History,
  BarChart3,
  TrendingUp,
  Settings,
  Sprout,
  Sparkles,
  X,
  ScrollText,
} from 'lucide-react';
import { AIAssistant } from './common/AIAssistant';

import { useThemeMode } from '../contexts/ThemeContext';
import { useConfigStore, useUIStore, selectAccentColour, selectHidePerformanceTab, selectActiveView, selectLoggingConfig } from '../stores';
import { useAuthStore, selectIsAdmin } from '../stores/authStore';
import { azureAuthService } from '../services/azureAuth';
import { DRAWER_WIDTH as TOKEN_DRAWER_WIDTH, APPBAR_HEIGHT_DESKTOP } from '../designTokens';
import type { ViewMode } from '../types';

// =============================================================================
// CONSTANTS
// =============================================================================
const DRAWER_WIDTH = TOKEN_DRAWER_WIDTH;
const COLLAPSED_DRAWER_WIDTH = 72;
const APPBAR_HEIGHT = APPBAR_HEIGHT_DESKTOP;

// Highland brand colours
const COLOURS = {
  navy: '#253746',
  orange: '#ed8b00',
  sidebarBg: '#ffffff',
  sidebarBorder: '#e5e7eb',
  contentBg: '#f9fafb',
  selectedBg: 'rgba(237, 139, 0, 0.08)',
  selectedText: '#253746',
  unselectedText: '#6b7280',
};

// =============================================================================
// HIGHLAND LOGO COMPONENT
// =============================================================================
const HighlandLogo = () => (
  <svg
    height="28"
    viewBox="0 0 1573.83 431.33"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="Highland logo — click to visit highland.health"
    style={{ cursor: 'pointer' }}
    onClick={() => window.open('https://highland.health', '_blank')}
  >
    <defs>
      <linearGradient id="highland-gradient" x1="0" y1="134.52" x2="369.94" y2="134.52" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#fe8c00"></stop>
        <stop offset="1" stopColor="#f83600"></stop>
        <animateTransform attributeName="gradientTransform" type="rotate" from="0 184.97 134.52" to="360 184.97 134.52" dur="8s" repeatCount="indefinite"></animateTransform>
      </linearGradient>
    </defs>
    <g>
      <path fill="url(#highland-gradient)" d="M365.48,53.68c-5.41-19.27-15.68-37.55-30.72-53.68,3.91,19.78,4.54,37.6,2.46,53.68-6.9,53.15-43.76,87-91.96,107.67-31.68,13.58-68.24,21.47-104.41,25.43-17.27,1.89-34.43,2.88-50.94,3.16-21.44.37-41.76-.45-59.68-2.07L0,265.5c20.35,1.98,39.81,3.08,58.43,3.43,17.46.32,34.15-.06,50.1-1.05,37.85-2.36,71.51-8.21,100.97-16.81,46.3-13.51,82.34-33.73,108.42-57.53,41.03-37.45,57.44-83.74,50.48-126.63-.72-4.45-1.69-8.87-2.91-13.23Z"></path>
      <path fill="#fff" d="M404.92,153.76l1.72,1.72v175.72l-1.72,1.72h-36.87l-1.72-1.72v-175.72l1.72-1.72h36.87ZM606.46,212.33c-23.78-16.19-37.56-21.71-63.74-21.71-29.29,0-54.44,22.74-54.44,52.72s23.43,52.71,54.44,52.71c11.03,0,19.98-1.03,28.6-5.51v-25.5h-26.88l-1.72-1.72v-33.77l1.72-1.72h65.47l1.72,1.72v86.48c-20.33,15.85-43.41,22.05-68.91,22.05-55.82,0-99.92-41.35-99.92-94.75s44.1-94.75,99.92-94.75c29.63,0,44.44,5.17,65.46,16.19v45.82l-1.72,1.72ZM689.13,153.76l1.73,1.72v60.64h83.72v-60.64l1.72-1.72h36.87l1.72,1.72v175.72l-1.72,1.72h-36.87l-1.72-1.72v-74.77h-83.72v74.77l-1.73,1.72h-36.86l-1.73-1.72v-175.72l1.73-1.72h36.86ZM898.59,153.76l1.72,1.72v137.13h75.8l1.73,1.72v36.87l-1.73,1.72h-114.39l-1.72-1.72v-175.72l1.72-1.72h36.87ZM1098.06,153.76l1.72,1.72,70.98,175.72-1.73,1.72h-40.66l-1.72-1.72-11.03-31.01h-68.91l-11.03,31.01-1.72,1.72h-40.66l-1.73-1.72,70.98-175.72,1.72-1.72h33.77ZM1060.5,262.98h41.34l-20.67-55.13-20.67,55.13ZM1237.57,153.76l3.45,1.72,83.72,110.25v-110.25l1.73-1.72h36.86l1.73,1.72v175.72l-1.73,1.72h-39.28l-3.45-1.72-83.38-110.25v110.25l-1.73,1.72h-36.86l-1.73-1.72v-175.72l1.73-1.72h38.93ZM1473.91,153.76c56.85,0,99.92,32.73,99.92,89.58s-43.06,89.58-99.92,89.58h-61.67l-1.72-1.72v-175.72l1.72-1.72h61.67ZM1450.83,292.61h23.08c33.42,0,54.44-16.19,54.44-49.27s-21.02-49.27-54.44-49.27h-23.08v98.54ZM165.24,125.59h-49.68l-25.65,64.35c16.51-.28,33.67-1.27,50.94-3.16l24.39-61.19ZM58.43,268.93l-30.34,76.12h49.68l30.76-77.17c-15.95.99-32.63,1.37-50.1,1.05ZM373.66,53.68h-8.18c1.22,4.36,2.19,8.78,2.91,13.23l5.27-13.23ZM137.64,431.33h85.49l94.78-237.79c-26.08,23.8-62.12,44.02-108.42,57.53l-71.85,180.26ZM337.22,53.68h-49.04l-42.92,107.67c48.21-20.67,85.06-54.52,91.96-107.67Z"></path>
    </g>
  </svg>
);

// =============================================================================
// NAVIGATION CONFIGURATION
// =============================================================================
interface NavItem {
  text: string;
  value: ViewMode;
  icon: React.ReactNode;
}

// Top section nav items (with divider after Content)
const topNavItems: NavItem[] = [
  { text: 'Content', value: 'content', icon: <FileText size={20} /> },
];

// Main section nav items (with divider after Analytics)
const mainNavItems: NavItem[] = [
  { text: 'Overview', value: 'overview', icon: <LayoutDashboard size={20} /> },
  { text: 'Calendar', value: 'calendar', icon: <Calendar size={20} /> },
  { text: 'Events', value: 'events', icon: <CalendarDays size={20} /> },
  { text: 'Categories', value: 'silos', icon: <FolderTree size={20} /> },
  { text: 'Performance', value: 'performance', icon: <BarChart3 size={20} /> },
];

// Admin section nav items (only visible to admins)
const adminNavItems: NavItem[] = [
  { text: 'Users', value: 'users', icon: <Users size={20} /> },
  { text: 'Audit Trail', value: 'audit', icon: <History size={20} /> },
];

// =============================================================================
// LAYOUT COMPONENT
// =============================================================================
const Layout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const isDrawerCollapsed = !isMobile && !isLargeScreen;

  const accentColour = useConfigStore(selectAccentColour);
  const hidePerformanceTab = useConfigStore(selectHidePerformanceTab);
  const loggingConfig = useConfigStore(selectLoggingConfig);
  const activeView = useUIStore(selectActiveView);

  // Auth state
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const isAdmin = useAuthStore(selectIsAdmin);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const { mode: themeMode, toggleTheme } = useThemeMode();

  // Filter main nav items based on config (Analytics visibility)
  const visibleMainItems = mainNavItems.filter(
    (item) => !(item.value === 'performance' && hidePerformanceTab)
  );

  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleLogout = async () => {
    handleMenuClose();
    // Clear local auth state first
    logout();
    // Then sign out from Azure AD (this may redirect)
    try {
      await azureAuthService.signOut();
    } catch (error) {
      console.error('Azure logout error:', error);
    }
    // Navigate to login page
    navigate('/login');
  };

  const handleNavClick = (view: ViewMode) => {
    useUIStore.getState().setActiveView(view);
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
    // Navigate to content page if not already there
    if (location.pathname !== '/' && location.pathname !== '/content') {
      navigate('/');
    }
  };

  // Check if we're on the content manager page
  const isContentPage = location.pathname === '/' || location.pathname === '/content';

  // =============================================================================
  // RENDER HELPERS
  // =============================================================================
  const renderNavItems = (items: NavItem[], collapsed = false) =>
    items.map((item) => {
      const isSelected = isContentPage && activeView === item.value;

      const button = (
        <ListItemButton
          key={item.text}
          selected={isSelected}
          onClick={() => handleNavClick(item.value)}
          sx={{
            mb: 0.5,
            mx: collapsed ? 0.5 : 1,
            borderRadius: 1.5,
            justifyContent: collapsed ? 'center' : 'flex-start',
            px: collapsed ? 1.5 : 2,
            py: 1,
            '&:hover': {
              backgroundColor: COLOURS.selectedBg,
            },
            '&.Mui-selected': {
              backgroundColor: COLOURS.selectedBg,
              borderLeft: collapsed ? 'none' : `3px solid ${accentColour}`,
              ml: collapsed ? 0.5 : 0,
              '&:hover': {
                backgroundColor: COLOURS.selectedBg,
              },
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: collapsed ? 'auto' : 36,
              color: isSelected ? accentColour : COLOURS.unselectedText,
            }}
          >
            {item.icon}
          </ListItemIcon>
          {!collapsed && (
            <ListItemText
              primary={
                <Typography
                  sx={{
                    margin: 0,
                    fontFamily: '"Open Sans", "Segoe UI", Arial, sans-serif',
                    lineHeight: 1.5,
                    fontWeight: isSelected ? 600 : 400,
                    color: isSelected ? COLOURS.selectedText : 'rgb(102, 102, 102)',
                    fontSize: '0.95rem',
                  }}
                >
                  {item.text}
                </Typography>
              }
            />
          )}
        </ListItemButton>
      );

      return collapsed ? (
        <Tooltip key={item.text} title={item.text} placement="right" arrow>
          {button}
        </Tooltip>
      ) : (
        <React.Fragment key={item.text}>{button}</React.Fragment>
      );
    });

  const getDrawerContent = (collapsed: boolean) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Top Navigation - Content */}
      <List sx={{ px: 0, pt: 2, pb: 1 }}>
        {renderNavItems(topNavItems, collapsed)}
      </List>

      <Divider sx={{ mx: collapsed ? 1 : 2 }} />

      {/* Main Navigation - Overview, Calendar, Categories, Performance */}
      <List sx={{ px: 0, py: 1 }}>
        {renderNavItems(visibleMainItems, collapsed)}
      </List>

      {/* External Analytics Link (GSC/GA data) - right after main nav */}
      <List sx={{ px: 0, py: 1 }}>
        {collapsed ? (
          <Tooltip title="Google Analytics" placement="right" arrow>
            <ListItemButton
              component={Link}
              to="/analytics"
              selected={location.pathname === '/analytics'}
              onClick={() => isMobile && setMobileDrawerOpen(false)}
              sx={{
                mx: 0.5,
                borderRadius: 1.5,
                justifyContent: 'center',
                px: 1.5,
                py: 1,
                '&:hover': {
                  backgroundColor: COLOURS.selectedBg,
                },
                '&.Mui-selected': {
                  backgroundColor: COLOURS.selectedBg,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 'auto', color: location.pathname === '/analytics' ? accentColour : COLOURS.unselectedText }}>
                <TrendingUp size={20} />
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        ) : (
          <ListItemButton
            component={Link}
            to="/analytics"
            selected={location.pathname === '/analytics'}
            onClick={() => isMobile && setMobileDrawerOpen(false)}
            sx={{
              mb: 0.5,
              mx: 1,
              borderRadius: 1.5,
              px: 2,
              py: 1,
              '&:hover': {
                backgroundColor: COLOURS.selectedBg,
              },
              '&.Mui-selected': {
                backgroundColor: COLOURS.selectedBg,
                borderLeft: `3px solid ${accentColour}`,
                ml: 0,
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: location.pathname === '/analytics' ? accentColour : COLOURS.unselectedText }}>
              <TrendingUp size={20} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography
                  sx={{
                    margin: 0,
                    fontFamily: '"Open Sans", "Segoe UI", Arial, sans-serif',
                    lineHeight: 1.5,
                    fontWeight: location.pathname === '/analytics' ? 600 : 400,
                    color: location.pathname === '/analytics' ? COLOURS.selectedText : 'rgb(102, 102, 102)',
                    fontSize: '0.95rem',
                  }}
                >
                  Google Analytics
                </Typography>
              }
            />
          </ListItemButton>
        )}
      </List>

      <Divider sx={{ mx: collapsed ? 1 : 2, my: 1 }} />

      {/* Admin Section - Only visible to admins */}
      {isAdmin && (
        <>
          {!collapsed && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 3,
                pt: 2,
                pb: 1,
              }}
            >
              <AdminPanelSettingsIcon
                sx={{
                  color: accentColour,
                  fontSize: '1.2rem',
                }}
              />
              <Typography
                sx={{
                  margin: 0,
                  fontFamily: '"Open Sans", "Segoe UI", Arial, sans-serif',
                  lineHeight: 1.5,
                  color: accentColour,
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Admin
              </Typography>
            </Box>
          )}
          <List sx={{ px: 0, py: collapsed ? 1 : 0 }}>
            {renderNavItems(adminNavItems, collapsed)}
          </List>
        </>
      )}

      {/* Settings Section - Only visible to admins */}
      {isAdmin && (
        <List sx={{ px: 0, py: collapsed ? 1 : 0, flex: 1 }}>
          {/* Settings Link */}
          {collapsed ? (
            <Tooltip title="Settings" placement="right" arrow>
              <ListItemButton
                component={Link}
                to="/settings"
                selected={location.pathname === '/settings' && !location.search.includes('tab=logging')}
                onClick={() => isMobile && setMobileDrawerOpen(false)}
                sx={{
                  mx: 0.5,
                  borderRadius: 1.5,
                  justifyContent: 'center',
                  px: 1.5,
                  py: 1,
                  '&:hover': {
                    backgroundColor: COLOURS.selectedBg,
                  },
                  '&.Mui-selected': {
                    backgroundColor: COLOURS.selectedBg,
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 'auto', color: COLOURS.unselectedText }}>
                  <Settings size={20} />
                </ListItemIcon>
              </ListItemButton>
            </Tooltip>
          ) : (
            <ListItemButton
              component={Link}
              to="/settings"
              selected={location.pathname === '/settings' && !location.search.includes('tab=logging')}
              onClick={() => isMobile && setMobileDrawerOpen(false)}
              sx={{
                mb: 0.5,
                mx: 1,
                borderRadius: 1.5,
                px: 2,
                py: 1,
                '&:hover': {
                  backgroundColor: COLOURS.selectedBg,
                },
                '&.Mui-selected': {
                  backgroundColor: COLOURS.selectedBg,
                  borderLeft: `3px solid ${accentColour}`,
                  ml: 0,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: location.pathname === '/settings' && !location.search.includes('tab=logging') ? accentColour : COLOURS.unselectedText }}>
                <Settings size={20} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    sx={{
                      margin: 0,
                      fontFamily: '"Open Sans", "Segoe UI", Arial, sans-serif',
                      lineHeight: 1.5,
                      fontWeight: location.pathname === '/settings' && !location.search.includes('tab=logging') ? 600 : 400,
                      color: location.pathname === '/settings' && !location.search.includes('tab=logging') ? COLOURS.selectedText : 'rgb(102, 102, 102)',
                      fontSize: '0.95rem',
                    }}
                  >
                    Settings
                  </Typography>
                }
              />
            </ListItemButton>
          )}

          {/* Logging Link - only shown when logging is enabled */}
          {loggingConfig.enabled && (
            <>
              {collapsed ? (
                <Tooltip title="Logging" placement="right" arrow>
                  <ListItemButton
                    component={Link}
                    to="/settings?tab=logging"
                    selected={location.pathname === '/settings' && location.search.includes('tab=logging')}
                    onClick={() => isMobile && setMobileDrawerOpen(false)}
                    sx={{
                      mx: 0.5,
                      borderRadius: 1.5,
                      justifyContent: 'center',
                      px: 1.5,
                      py: 1,
                      '&:hover': {
                        backgroundColor: COLOURS.selectedBg,
                      },
                      '&.Mui-selected': {
                        backgroundColor: COLOURS.selectedBg,
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 'auto', color: location.pathname === '/settings' && location.search.includes('tab=logging') ? accentColour : COLOURS.unselectedText }}>
                      <ScrollText size={20} />
                    </ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              ) : (
                <ListItemButton
                  component={Link}
                  to="/settings?tab=logging"
                  selected={location.pathname === '/settings' && location.search.includes('tab=logging')}
                  onClick={() => isMobile && setMobileDrawerOpen(false)}
                  sx={{
                    mb: 0.5,
                    mx: 1,
                    borderRadius: 1.5,
                    px: 2,
                    py: 1,
                    '&:hover': {
                      backgroundColor: COLOURS.selectedBg,
                    },
                    '&.Mui-selected': {
                      backgroundColor: COLOURS.selectedBg,
                      borderLeft: `3px solid ${accentColour}`,
                      ml: 0,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: location.pathname === '/settings' && location.search.includes('tab=logging') ? accentColour : COLOURS.unselectedText }}>
                    <ScrollText size={20} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          margin: 0,
                          fontFamily: '"Open Sans", "Segoe UI", Arial, sans-serif',
                          lineHeight: 1.5,
                          fontWeight: location.pathname === '/settings' && location.search.includes('tab=logging') ? 600 : 400,
                          color: location.pathname === '/settings' && location.search.includes('tab=logging') ? COLOURS.selectedText : 'rgb(102, 102, 102)',
                          fontSize: '0.95rem',
                        }}
                      >
                        Logging
                      </Typography>
                    }
                  />
                </ListItemButton>
              )}
            </>
          )}
        </List>
      )}
    </Box>
  );

  // =============================================================================
  // RENDER
  // =============================================================================
  return (
    <Box sx={{ display: 'flex' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: COLOURS.navy,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            {/* Mobile menu button */}
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Logo */}
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <HighlandLogo />
            </Box>
          </Box>

          {/* Center - App Title with Icon */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: '6px',
                bgcolor: 'rgba(255,255,255,0.15)',
              }}
            >
              <Sprout size={16} color="#fff" />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                letterSpacing: '0.03em',
                textTransform: 'uppercase',
                fontSize: { xs: '0.8rem', md: '1rem' },
                color: 'rgba(255,255,255,0.9)',
              }}
            >
              Content Manager
            </Typography>
          </Box>

          {/* Right - AI Assistant & User menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* AI Assistant Button */}
            <Tooltip title="AI Assistant" arrow>
              <IconButton
                color="inherit"
                onClick={() => setAiPanelOpen(true)}
                sx={{
                  bgcolor: aiPanelOpen ? 'rgba(255,255,255,0.15)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                <Sparkles size={20} />
              </IconButton>
            </Tooltip>

            <IconButton size="large" onClick={handleMenuOpen} color="inherit" sx={{ p: 0.5 }}>
              <Avatar
                src={user?.avatarUrl || undefined}
                sx={{
                  bgcolor: accentColour,
                  width: 36,
                  height: 36,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                }}
              >
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
            </IconButton>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              {/* User info header */}
              {user && (
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e5e7eb' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {user.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user.email}
                  </Typography>
                </Box>
              )}
              <MenuItem
                aria-label={themeMode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
                onClick={() => {
                  handleMenuClose();
                  toggleTheme();
                }}
              >
                {themeMode === 'light' ? (
                  <DarkModeIcon sx={{ mr: 1 }} fontSize="small" />
                ) : (
                  <LightModeIcon sx={{ mr: 1 }} fontSize="small" />
                )}
                {themeMode === 'light' ? 'Dark Mode' : 'Light Mode'}
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1 }} fontSize="small" />
                Sign out
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileDrawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            backgroundColor: COLOURS.sidebarBg,
            borderRight: `1px solid ${COLOURS.sidebarBorder}`,
            mt: `${APPBAR_HEIGHT}px`,
            height: `calc(100% - ${APPBAR_HEIGHT}px)`,
          },
        }}
      >
        {getDrawerContent(false)}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: isDrawerCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH,
          flexShrink: 0,
          transition: 'width 0.2s ease-in-out',
          '& .MuiDrawer-paper': {
            width: isDrawerCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH,
            boxSizing: 'border-box',
            backgroundColor: COLOURS.sidebarBg,
            borderRight: `1px solid ${COLOURS.sidebarBorder}`,
            mt: `${APPBAR_HEIGHT}px`,
            transition: 'width 0.2s ease-in-out',
            overflowX: 'hidden',
          },
        }}
      >
        {getDrawerContent(isDrawerCollapsed)}
      </Drawer>

      {/* Main Content */}
      <Box
        id="main-content"
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: COLOURS.contentBg,
          mt: `${APPBAR_HEIGHT}px`,
          minHeight: `calc(100vh - ${APPBAR_HEIGHT}px)`,
          width: {
            xs: '100%',
            md: `calc(100% - ${isDrawerCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH}px)`,
          },
          transition: 'width 0.2s ease-in-out',
        }}
      >
        <Outlet />
      </Box>

      {/* AI Assistant Panel */}
      <Drawer
        anchor="right"
        open={aiPanelOpen}
        onClose={() => setAiPanelOpen(false)}
        role="dialog"
        aria-modal="true"
        aria-label="AI Assistant"
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 450 },
            boxSizing: 'border-box',
            mt: `${APPBAR_HEIGHT}px`,
            height: `calc(100% - ${APPBAR_HEIGHT}px)`,
          },
        }}
      >
        <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Sparkles size={20} color={accentColour} />
              AI Assistant
            </Typography>
            <IconButton onClick={() => setAiPanelOpen(false)} size="small" aria-label="Close AI Assistant">
              <X size={20} />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <AIAssistant />
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default Layout;
